/**
 * Delays as they arrive from their source, what state each movement is in on
 * the ramp, and what a delay does to the stands and gates planned after it.
 *
 * ── where a delay comes from ────────────────────────────────────────────
 *
 * A delay reaches the airport as a NOTICE: the airline's operations control
 * sends a new time, a ground handler reports a late turn, an ATC flow message
 * gives a slot, the schedule feed changes, or the airport's own duty manager
 * records one. Each notice has a moment it was received and the time it
 * announced. Keeping them — not only the latest estimate — answers the
 * question that decides whether a stand plan survives the day: how much
 * warning did we have? An hour's notice is a re-plan; five minutes is a
 * scramble; a notice that arrives after the scheduled time is no notice.
 *
 * ── the state on the ramp ───────────────────────────────────────────────
 *
 * For a departure the moment that matters is the door: boarding runs until
 * the doors close a few minutes before off-block. A departure whose doors
 * should have closed by now and has not left is the one the ramp must look
 * at first — red. One that is boarding is yellow. The thresholds are
 * PROPOSED, like the board's own.
 *
 * ── the knock-on ────────────────────────────────────────────────────────
 *
 * A late arrival reaches its stand later and, because the turn runs from
 * when it arrives, leaves it later; a late departure holds it longer. So each
 * planned stand occupation is shifted by the delay its movements now carry,
 * and a shifted occupation that runs into the next one on the same stand is
 * a collision the plan did not have. The same for gates: a late departure
 * boards later and can land on top of the next one at its gate. Each
 * collision names both movements, the overlap, and — for a stand — the first
 * stand that is free for the one pushed out.
 *
 * Pure: `now` is always passed in.
 */
import { t } from "../i18n";
import { BOARDING_FROM } from "./fids";
import { bestKnown, delayMinutes, flightName, type Flight } from "./flights";
import { candidatesFor, type StandAssignment, type StandBlock, type StandRegister } from "./stands";

export type DelaySource = "airline_occ" | "ground_handler" | "atc" | "schedule_feed" | "airport_ops";

export const SOURCE_LABEL: Record<DelaySource, string> = {
  airline_occ: "Airline operations control",
  ground_handler: "Ground handler",
  atc: "ATC flow message",
  schedule_feed: "Schedule feed",
  airport_ops: "Airport duty manager",
};

export interface DelayNotice {
  /** when the airport received it */
  at: string;
  source: DelaySource;
  /** who, where known: "XY OCC", a handler's supervisor */
  by?: string;
  /** the new time it announced */
  newTime: string;
  code?: string;
  note?: string;
}

/** How much warning there was. PROPOSED thresholds. */
export type Warning = "early" | "short" | "after" | "none";
export const EARLY_WARNING_MINUTES = 60;

export const WARNING_LABEL: Record<Warning, string> = {
  early: "Early warning",
  short: "Short notice",
  after: "After the scheduled time",
  none: "No notice received",
};

export interface DelayStory {
  flight: Flight;
  minutes: number | null;
  notices: DelayNotice[];
  first: DelayNotice | null;
  latest: DelayNotice | null;
  /** minutes between the first notice and the scheduled time; negative when after */
  leadMinutes: number | null;
  warning: Warning;
  revisions: number;
}

const ms = (s?: string | null) => (s ? Date.parse(s) : NaN);

export function noticesOf(f: Flight): DelayNotice[] {
  return [...(f.notices ?? [])].sort((a, b) => a.at.localeCompare(b.at));
}

/** One movement's delay: how late, who said so, when, and how much warning that was. */
export function delayStory(f: Flight): DelayStory {
  const notices = noticesOf(f);
  const first = notices[0] ?? null;
  const latest = notices.at(-1) ?? null;
  const lead = first && f.scheduledAt ? Math.round((ms(f.scheduledAt) - ms(first.at)) / 60_000) : null;
  const warning: Warning = !first
    ? "none"
    : lead == null || lead < 0
      ? "after"
      : lead >= EARLY_WARNING_MINUTES
        ? "early"
        : "short";
  return {
    flight: f,
    minutes: delayMinutes(f),
    notices,
    first,
    latest,
    leadMinutes: lead,
    warning,
    revisions: Math.max(0, notices.length - 1),
  };
}

/** The movements carrying a delay worth planning around (15 minutes or more), worst first. */
export function delayedStories(flights: readonly Flight[]): DelayStory[] {
  return flights
    .filter((f) => f.source !== "forecast" && !f.cancelled)
    .map(delayStory)
    .filter((s) => (s.minutes ?? 0) >= 15 || s.notices.length > 0)
    .sort((a, b) => (b.minutes ?? 0) - (a.minutes ?? 0));
}

export interface DelaySummary {
  delayed: number;
  stillToCome: number;
  early: number;
  short: number;
  after: number;
  none: number;
  averageLead: number | null;
  bySource: { source: DelaySource; count: number }[];
  minutes: number;
}

export function delaySummary(stories: readonly DelayStory[]): DelaySummary {
  const leads = stories.map((s) => s.leadMinutes).filter((x): x is number => x != null);
  const bySource = new Map<DelaySource, number>();
  for (const s of stories) for (const n of s.notices) bySource.set(n.source, (bySource.get(n.source) ?? 0) + 1);
  return {
    delayed: stories.filter((s) => (s.minutes ?? 0) >= 15).length,
    stillToCome: stories.filter((s) => !s.flight.actualAt && (s.minutes ?? 0) >= 15).length,
    early: stories.filter((s) => s.warning === "early").length,
    short: stories.filter((s) => s.warning === "short").length,
    after: stories.filter((s) => s.warning === "after").length,
    none: stories.filter((s) => s.warning === "none").length,
    averageLead: leads.length ? Math.round(leads.reduce((a, b) => a + b, 0) / leads.length) : null,
    bySource: [...bySource.entries()].map(([source, count]) => ({ source, count })).sort((a, b) => b.count - a.count),
    minutes: stories.reduce((a, s) => a + Math.max(0, s.minutes ?? 0), 0),
  };
}

/* ── the state on the ramp ─────────────────────────────────────────────── */

/** Doors close this many minutes before the departure time. PROPOSED. */
export const DOOR_CLOSE_BEFORE = 5;

export type OpsState =
  "door_late" | "boarding" | "delayed" | "on_time" | "scheduled" | "landed" | "departed" | "cancelled";

export const OPS_LABEL: Record<OpsState, string> = {
  door_late: "Doors late",
  boarding: "Boarding",
  delayed: "Delayed",
  on_time: "On time",
  scheduled: "Scheduled",
  landed: "Landed",
  departed: "Departed",
  cancelled: "Canceled",
};

export function opsState(f: Flight, now: Date): OpsState {
  if (f.cancelled) return "cancelled";
  if (f.actualAt) return f.direction === "arrival" ? "landed" : "departed";
  const best = bestKnown(f);
  const at = ms(best.at);
  const late = (delayMinutes(f) ?? 0) >= 15;
  if (f.direction === "departure" && !Number.isNaN(at)) {
    const away = (at - now.getTime()) / 60_000;
    if (away <= DOOR_CLOSE_BEFORE) return "door_late";
    if (away <= BOARDING_FROM) return "boarding";
  }
  if (late) return "delayed";
  if (!Number.isNaN(at) && at < now.getTime()) return "delayed";
  return (delayMinutes(f) ?? null) !== null ? "on_time" : "scheduled";
}

/** Minutes a departure is past the point its doors should have closed. */
export function doorsLateBy(f: Flight, now: Date): number | null {
  if (f.direction !== "departure" || f.actualAt || f.cancelled) return null;
  const at = ms(bestKnown(f).at);
  if (Number.isNaN(at)) return null;
  const over = Math.round((now.getTime() - (at - DOOR_CLOSE_BEFORE * 60_000)) / 60_000);
  return over >= 0 ? over : null;
}

/* ── the knock-on on stands and gates ──────────────────────────────────── */

export interface Impact {
  kind: "stand" | "gate";
  /** the stand code or the gate */
  where: string;
  /** the delayed movement that now runs over */
  causeId: string;
  cause: string;
  /** the movement it runs into */
  victimId: string;
  victim: string;
  overlapMinutes: number;
  /** when the delayed movement now leaves the stand, or finishes boarding */
  until: string;
  /** the first free stand for the one pushed out, when there is one */
  suggestion?: { stand: string; because: string };
}

function pairOf(f: Flight, flights: readonly Flight[]): Flight | undefined {
  return f.turnaround ? flights.find((x) => x.turnaround === f.turnaround && x.id !== f.id) : undefined;
}

const lateBy = (f?: Flight) => (f && !f.actualAt ? Math.max(0, delayMinutes(f) ?? 0) : 0);

/** An assignment as the delays now make it: later on, and later off. */
export function shiftedWindow(
  a: StandAssignment,
  flights: readonly Flight[],
): { from: number; to: number; shift: number } {
  const f = flights.find((x) => x.id === a.flightId);
  const pair = f ? pairOf(f, flights) : undefined;
  const arrival = f?.direction === "arrival" ? f : pair?.direction === "arrival" ? pair : undefined;
  const departure = f?.direction === "departure" ? f : pair?.direction === "departure" ? pair : undefined;
  const arrLate = a.actual ? 0 : lateBy(arrival);
  const shift = Math.max(arrLate, lateBy(departure));
  return { from: ms(a.from) + arrLate * 60_000, to: ms(a.to) + shift * 60_000, shift };
}

export function delayImpacts(
  flights: readonly Flight[],
  register: StandRegister,
  blocks: readonly StandBlock[],
  now: Date,
): Impact[] {
  const out: Impact[] = [];
  const byId = new Map(flights.map((f) => [f.id, f]));
  const name = (id: string) => {
    const f = byId.get(id);
    return f ? flightName(f) : id;
  };
  /* stands */
  const byStand = new Map<string, StandAssignment[]>();
  for (const a of register.assignments) byStand.set(a.standCode, [...(byStand.get(a.standCode) ?? []), a]);
  for (const [stand, list] of byStand) {
    const sorted = [...list].sort((x, y) => x.from.localeCompare(y.from));
    for (let i = 0; i < sorted.length; i++) {
      const a = sorted[i];
      const wa = shiftedWindow(a, flights);
      if (wa.shift < 15 || wa.to < now.getTime()) continue;
      for (let j = i + 1; j < sorted.length; j++) {
        const b = sorted[j];
        if (ms(a.to) > ms(b.from)) continue; /* already colliding in the plan: the stand board reports it */
        const wb = shiftedWindow(b, flights);
        const overlap = Math.round((wa.to - wb.from) / 60_000);
        if (overlap <= 0) continue;
        const victim = byId.get(b.flightId);
        let suggestion: Impact["suggestion"];
        if (victim) {
          const extended: StandRegister = {
            ...register,
            assignments: register.assignments.map((x) =>
              x.id === a.id ? { ...x, to: new Date(wa.to).toISOString() } : x,
            ),
          };
          const best = candidatesFor({
            register: extended,
            flights,
            blocks,
            flight: victim,
            window: { from: new Date(wb.from).toISOString(), to: new Date(wb.to).toISOString() },
            ignoreAssignmentId: b.id,
          }).find((c) => c.clear && c.stand.code !== stand);
          if (best) suggestion = { stand: best.stand.code, because: best.because };
        }
        out.push({
          kind: "stand",
          where: stand,
          causeId: a.flightId,
          cause: name(a.flightId),
          victimId: b.flightId,
          victim: name(b.flightId),
          overlapMinutes: overlap,
          until: new Date(wa.to).toISOString(),
          suggestion,
        });
      }
    }
  }
  /* gates: a departure boards for BOARDING_FROM minutes up to its time */
  const gateOf = (f: Flight): string | undefined => {
    /* the flight's own gate where the allocation placed one, else its stand's */
    if (f.gate) return f.gate;
    const pair = pairOf(f, flights);
    const a =
      register.assignments.find((x) => x.flightId === f.id) ??
      (pair ? register.assignments.find((x) => x.flightId === pair.id) : undefined);
    return a ? register.stands.find((s) => s.code === a.standCode)?.gate : undefined;
  };
  const deps = flights.filter(
    (f) => f.direction === "departure" && !f.actualAt && !f.cancelled && f.source !== "forecast",
  );
  const byGate = new Map<string, Flight[]>();
  for (const f of deps) {
    const g = gateOf(f);
    if (g) byGate.set(g, [...(byGate.get(g) ?? []), f]);
  }
  for (const [gate, list] of byGate) {
    for (const a of list) {
      if (lateBy(a) < 15) continue;
      const aEnd = ms(bestKnown(a).at);
      for (const b of list) {
        if (a === b) continue;
        const bSched = ms(b.scheduledAt);
        if (!(ms(a.scheduledAt) <= bSched)) continue;
        const bStart = ms(bestKnown(b).at) - BOARDING_FROM * 60_000;
        const overlap = Math.round((aEnd - bStart) / 60_000);
        if (overlap > 0 && aEnd > now.getTime())
          out.push({
            kind: "gate",
            where: gate,
            causeId: a.id,
            cause: flightName(a),
            victimId: b.id,
            victim: flightName(b),
            overlapMinutes: overlap,
            until: new Date(aEnd).toISOString(),
          });
      }
    }
  }
  return out.sort((x, y) => y.overlapMinutes - x.overlapMinutes);
}

export function impactSentence(i: Impact): string {
  return i.kind === "stand"
    ? t("{cause} now holds stand {where} until {until}, {m} minutes into {victim}'s slot.", {
        cause: i.cause,
        where: i.where,
        until: new Date(i.until).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        m: i.overlapMinutes,
        victim: i.victim,
      })
    : t("{cause} now boards at gate {where} until {until}, {m} minutes into {victim}'s boarding.", {
        cause: i.cause,
        where: i.where,
        until: new Date(i.until).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        m: i.overlapMinutes,
        victim: i.victim,
      });
}
